import React, { useRef, useState } from "react";
import {
  FaBold,
  FaItalic,
  FaHeading,
  FaListUl,
  FaListOl,
  FaLink,
  FaEye,
  FaPen,
} from "react-icons/fa";
import { SessionNotesMarkdown } from "./SessionNotesMarkdown";

interface SessionNotesEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
}

type WrapAction = { type: "wrap"; before: string; after: string };
type LinePrefixAction = {
  type: "linePrefix";
  prefix: string;
  ordered?: boolean;
};
type ToolbarAction = WrapAction | LinePrefixAction;

// Lightweight markdown editor for authoring a session's notes. Mirrors the
// coach portal's editor so notes look identical on both sides, and renders a
// live preview via the shared SessionNotesMarkdown renderer.
const SessionNotesEditor: React.FC<SessionNotesEditorProps> = ({
  value,
  onChange,
  readOnly = false,
  placeholder = "Write your session notes here...",
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showPreview, setShowPreview] = useState(false);

  if (readOnly) {
    return value?.trim() ? (
      <SessionNotesMarkdown content={value} />
    ) : (
      <p className="text-base-content/50 italic font-grotesk">No notes.</p>
    );
  }

  const applyAction = (action: ToolbarAction) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.slice(start, end);

    let newValue: string;
    let nextSelStart: number;
    let nextSelEnd: number;

    if (action.type === "wrap") {
      const inner = selected || "text";
      const replacement = `${action.before}${inner}${action.after}`;
      newValue = value.slice(0, start) + replacement + value.slice(end);
      nextSelStart = start + action.before.length;
      nextSelEnd = nextSelStart + inner.length;
    } else {
      // Prefix each selected line (or the current line) with a markdown marker.
      const lineStart = value.lastIndexOf("\n", start - 1) + 1;
      const block = value.slice(lineStart, end);
      const lines = block.split("\n");
      const prefixed = lines
        .map((line, i) =>
          action.ordered ? `${i + 1}. ${line}` : `${action.prefix}${line}`,
        )
        .join("\n");
      newValue = value.slice(0, lineStart) + prefixed + value.slice(end);
      nextSelStart = lineStart;
      nextSelEnd = lineStart + prefixed.length;
    }

    onChange(newValue);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(nextSelStart, nextSelEnd);
    });
  };

  const toolbarButtons: {
    label: string;
    icon: React.ReactNode;
    action: ToolbarAction;
  }[] = [
    {
      label: "Bold",
      icon: <FaBold />,
      action: { type: "wrap", before: "**", after: "**" },
    },
    {
      label: "Italic",
      icon: <FaItalic />,
      action: { type: "wrap", before: "_", after: "_" },
    },
    {
      label: "Heading",
      icon: <FaHeading />,
      action: { type: "linePrefix", prefix: "## " },
    },
    {
      label: "Bulleted list",
      icon: <FaListUl />,
      action: { type: "linePrefix", prefix: "- " },
    },
    {
      label: "Numbered list",
      icon: <FaListOl />,
      action: { type: "linePrefix", prefix: "", ordered: true },
    },
    {
      label: "Link",
      icon: <FaLink />,
      action: { type: "wrap", before: "[", after: "](https://)" },
    },
  ];

  return (
    <div className="border border-base-300 rounded-lg overflow-hidden bg-base-100">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-base-300 bg-base-200">
        {toolbarButtons.map((btn) => (
          <button
            key={btn.label}
            type="button"
            title={btn.label}
            aria-label={btn.label}
            onClick={() => applyAction(btn.action)}
            className="p-2 rounded hover:bg-base-300 text-base-content/70 hover:text-base-content transition-colors"
          >
            {btn.icon}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setShowPreview((p) => !p)}
          className="ml-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded hover:bg-base-300 text-base-content/70 hover:text-base-content transition-colors text-sm font-grotesk"
        >
          {showPreview ? <FaPen /> : <FaEye />}
          {showPreview ? "Edit" : "Preview"}
        </button>
      </div>

      {showPreview ? (
        <div className="p-4 min-h-[10rem]">
          {value?.trim() ? (
            <SessionNotesMarkdown content={value} />
          ) : (
            <p className="text-base-content/50 italic font-grotesk">
              Nothing to preview yet.
            </p>
          )}
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full min-h-[10rem] p-4 bg-base-100 text-base-content font-grotesk resize-y focus:outline-none text-[16px] sm:text-sm"
        />
      )}
    </div>
  );
};

export default SessionNotesEditor;
